import type { ReactNode } from 'react';

interface StatCardProps {
  label: string;
  value: string;
  /** Muted context line under the value (e.g. "avg order ₹1,546"). */
  subValue?: string;
  /**
   * Signed change vs the previous period, in percent. Positive renders as
   * success ink, negative as danger ink. Omit when there is no comparison.
   */
  trend?: number;
  /** Muted suffix after the delta, e.g. "vs previous 30 days". */
  trendLabel?: string;
  icon: ReactNode;
  /**
   * @deprecated v2 KPI tiles all use the accent-soft chip; kept so existing
   * callers compile. Ignored.
   */
  accent?: string;
}

/**
 * Dashboard KPI tile — design system v2 §04c `.kpi`: muted label with an
 * accent-soft icon chip, a 30/700 tabular value, and a delta line that
 * compares against the previous period.
 */
export function StatCard(props: StatCardProps) {
  const { label, value, subValue, trend, trendLabel, icon } = props;
  const dir = trend === undefined ? null : trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat';
  return (
    <div className="panel p-[18px]">
      <div className="flex items-center justify-between gap-3 text-[13.5px] font-medium text-ink-500">
        <span className="min-w-0 truncate">{label}</span>
        <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-admin-soft text-admin-ink">
          {icon}
        </span>
      </div>
      <p className="font-tabular mb-1 mt-2.5 text-kpi text-ink-900">{value}</p>
      {dir ? (
        <>
          <p className="text-[13px] font-semibold">
            <span
              className={
                dir === 'up' ? 'text-success-ink' : dir === 'down' ? 'text-danger-ink' : 'text-ink-700'
              }
            >
              {dir === 'up' ? '+' : dir === 'down' ? '−' : ''}
              {Math.abs(trend!)}%
            </span>{' '}
            {trendLabel && <span className="font-normal text-ink-500">{trendLabel}</span>}
          </p>
          {subValue && <p className="mt-0.5 text-[13px] text-ink-500">{subValue}</p>}
        </>
      ) : (
        subValue && <p className="text-[13px] text-ink-500">{subValue}</p>
      )}
    </div>
  );
}
